import {
  AcademicSystem,
  CourseEntry,
  MajorRequirement,
  MajorSummary,
  Requirement,
  RequirementSummary,
  YearPlan,
} from '../types';

export const termsForSystem = (system: AcademicSystem | null): string[] => {
  if (system === 'quarter') return ['Fall', 'Winter', 'Spring', 'Summer'];
  return ['Fall', 'Spring', 'Summer'];
};

export const activeTermsPerYear = (system: AcademicSystem | null): number => {
  if (system === 'quarter') return 3;
  if (system === 'semester') return 2;
  return 1;
};

export const condensedLabel = (startYear: number, offset: number) => {
  const start = (startYear + offset).toString().slice(-2).padStart(2, '0');
  const end = (startYear + offset + 1).toString().slice(-2).padStart(2, '0');
  return `${start}-${end}`;
};

export const makeYearPlan = (index: number, terms: string[]): YearPlan => ({
  id: `year-${index}-${Math.random().toString(16).slice(2, 6)}`,
  label: `Year ${index}`,
  terms: terms.map((term) => ({
    term,
    completed: false,
    courses: [
      {
        id: `course-1-${Math.random().toString(16).slice(2, 5)}`,
        name: '',
        distributives: [],
        requiredCourseId: '',
        isMajorRequirement: false,
      },
    ],
  })),
});

export const buildCoursesForTerm = (count: string): CourseEntry[] => {
  const numeric = Number(count);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return [
      {
        id: `course-1-${Math.random().toString(16).slice(2, 5)}`,
        name: '',
        distributives: [],
        requiredCourseId: '',
        isMajorRequirement: false,
      },
    ];
  }
  return Array.from({ length: numeric }, (_, courseIndex) => ({
    id: `course-${courseIndex + 1}-${Math.random().toString(16).slice(2, 5)}`,
    name: '',
    distributives: [],
    requiredCourseId: '',
    isMajorRequirement: false,
  }));
};

export const computeRequirementSummaries = (
  yearPlans: YearPlan[],
  requirements: Requirement[],
): RequirementSummary[] =>
  requirements
    .map((req) => {
      const total = Number(req.count);
      if (!req.name.trim() || !Number.isFinite(total) || total < 0) return null;
      const fulfilled: Array<{ course: string; term: string; year: string }> = [];
      yearPlans.forEach((year) =>
        year.terms.forEach((termPlan) =>
          termPlan.courses.forEach((course) => {
            if (course.distributives.includes(req.name)) {
              fulfilled.push({
                course: course.name.trim() || 'Untitled course',
                term: termPlan.term,
                year: year.label,
              });
            }
          })
        )
      );
      const remaining = Math.max(total - fulfilled.length, 0);
      return { name: req.name, total, fulfilled, remaining };
    })
    .filter((item): item is RequirementSummary => Boolean(item));

export const computeMajorSummaries = (
  yearPlans: YearPlan[],
  majors: MajorRequirement[],
  majorClassLookup: Record<string, { major: string; name: string }>,
  defaultLabel: string = 'Major',
): MajorSummary[] =>
  majors
    .map((major) => {
      const required = major.classes.filter((cls) => cls.name.trim() !== '');
      if (required.length === 0) return null;
      const satisfiedIds = new Set<string>();
      const fulfilled: Array<{ course: string; term: string; year: string }> = [];
      yearPlans.forEach((year) =>
      year.terms.forEach((termPlan) =>
        termPlan.courses.forEach((course) => {
          if (course.isMajorRequirement && course.requiredCourseId && majorClassLookup[course.requiredCourseId]) {
            const requirement = majorClassLookup[course.requiredCourseId];
            if (requirement) {
              satisfiedIds.add(course.requiredCourseId);
              fulfilled.push({
                course: requirement.name,
                  term: termPlan.term,
                  year: year.label,
                });
              }
            }
          })
        )
      );
      const remaining = Math.max(required.length - satisfiedIds.size, 0);
      return { name: major.name || defaultLabel, total: required.length, fulfilled, remaining };
    })
    .filter((item): item is MajorSummary => Boolean(item));
